import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, Mail, Plus, Trash2, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { userGroupsApi, type UserGroup } from '@/api/userGroupsApi'
import { tenantsApi } from '@/api/tenantsApi'
import { useUserGroups } from '@/hooks/useSigners'
import { useTenant } from '@/providers/TenantProvider'
import { useSearchParams } from 'react-router-dom'
import { EmailTemplatesSettings } from './EmailTemplatesSettings'

/**
 * Configure → User Groups (z8nrz7dt45): the one place groups are managed.
 *
 * Forms reference groups by id — a signature field's allowed signers, a form's
 * allowed completers — so changing who is in "QA Review" here changes who may
 * sign every form that names it. Every change is audited server-side, and a
 * group still named by a form cannot be deleted (the error says which form).
 *
 * Tenant-scoped: groups and their candidate members are the selected tenant's.
 * Gated on users.manage in that tenant, the same key as the members panel.
 */
export function UserGroupsSettings() {
  const [params, setParams] = useSearchParams()
  const view: EmailGroupsView = params.get('view') === 'templates' ? 'templates' : 'groups'
  const show = (next: EmailGroupsView) =>
    setParams(
      prev => {
        const p = new URLSearchParams(prev)
        if (next === 'groups') {
          p.delete('view')
          p.delete('email')
        } else p.set('view', next)
        return p
      },
      { replace: true },
    )

  return (
    <div className='space-y-5'>
      {/* A segmented control, not a nested Tabs: the Configure page already
          has a tab list, and a second one would confuse role-based locators. */}
      <div role='group' aria-label='Email Groups sections' className='inline-flex rounded-lg border bg-muted/40 p-1'>
        {EMAIL_GROUPS_VIEWS.map(v => (
          <button
            key={v.value}
            type='button'
            aria-pressed={view === v.value}
            onClick={() => show(v.value)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors',
              view === v.value
                ? 'bg-background font-medium text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <v.icon className='h-3.5 w-3.5' />
            {v.label}
          </button>
        ))}
      </div>
      {view === 'groups' ? <GroupsSection /> : <EmailTemplatesSettings />}
    </div>
  )
}

type EmailGroupsView = 'groups' | 'templates'

const EMAIL_GROUPS_VIEWS: { value: EmailGroupsView; label: string; icon: typeof Users }[] = [
  { value: 'groups', label: 'Groups', icon: Users },
  { value: 'templates', label: 'Email templates', icon: Mail },
]

/** The groups and their members. */
function GroupsSection() {
  const { selectedTenantId } = useTenant()
  const queryClient = useQueryClient()
  const groups = useUserGroups()
  const members = useQuery({
    queryKey: ['tenant-members', selectedTenantId],
    enabled: !!selectedTenantId,
    queryFn: () => tenantsApi.listMembers(selectedTenantId as string),
  })

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [newName, setNewName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const selected = groups.data?.find(g => g.id === selectedId) ?? null

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['user-groups', selectedTenantId] })
  const onError = (e: unknown) => setError(e instanceof Error ? e.message : 'Something went wrong.')

  const create = useMutation({
    mutationFn: (name: string) => userGroupsApi.create({ name }),
    onSuccess: g => {
      setNewName('')
      setSelectedId(g.id)
      setError(null)
      void refresh()
    },
    onError,
  })

  return (
    <div className='space-y-6'>
      <div className='grid gap-6 lg:grid-cols-[18rem_1fr]'>
        <Card>
          <CardContent className='space-y-3 p-4'>
            <p className='text-sm font-medium'>Email groups</p>
            <form
              className='flex gap-2'
              onSubmit={e => {
                e.preventDefault()
                if (newName.trim()) create.mutate(newName.trim())
              }}
            >
              <Input
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder='New group, e.g. QA Review'
                aria-label='New group name'
                className='h-8 text-sm'
              />
              <Button type='submit' size='sm' className='h-8' disabled={!newName.trim() || create.isPending}>
                <Plus className='h-3.5 w-3.5' />
              </Button>
            </form>
            {groups.isLoading && <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />}
            {groups.data?.length === 0 && (
              <p className='text-xs text-muted-foreground'>
                No groups yet. Create one, add its members, then restrict a signature field or a form
                to it in the form builder.
              </p>
            )}
            <ul className='space-y-0.5'>
              {groups.data?.map(g => (
                <li key={g.id}>
                  <button
                    type='button'
                    onClick={() => {
                      setSelectedId(g.id)
                      setError(null)
                    }}
                    className={cn(
                      'flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent',
                      g.id === selectedId && 'bg-accent font-medium',
                    )}
                  >
                    <span className='truncate'>{g.name}</span>
                    <span className='flex items-center gap-1 text-xs text-muted-foreground'>
                      <Users className='h-3 w-3' /> {g.members.length}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <div className='space-y-3'>
          {error && <p className='text-sm text-destructive'>{error}</p>}
          {selected ? (
            <GroupEditor
              key={selected.id}
              group={selected}
              candidates={(members.data ?? []).map(m => ({
                id: m.user.id,
                name: m.user.fullName || m.user.email,
                email: m.user.email,
                enabled: m.user.enabled,
              }))}
              candidatesLoading={members.isLoading}
              onSaved={() => {
                setError(null)
                void refresh()
              }}
              onDeleted={() => {
                setSelectedId(null)
                setError(null)
                void refresh()
              }}
              onError={onError}
            />
          ) : (
            <p className='text-sm text-muted-foreground'>Select a group to edit its members.</p>
          )}
        </div>
      </div>
    </div>
  )
}

function GroupEditor({
  group,
  candidates,
  candidatesLoading,
  onSaved,
  onDeleted,
  onError,
}: {
  group: UserGroup
  candidates: { id: string; name: string; email: string; enabled: boolean }[]
  candidatesLoading: boolean
  onSaved: () => void
  onDeleted: () => void
  onError: (e: unknown) => void
}) {
  const [name, setName] = useState(group.name)
  const [description, setDescription] = useState(group.description ?? '')
  const [memberIds, setMemberIds] = useState<Set<string>>(() => new Set(group.members.map(m => m.id)))
  const [filter, setFilter] = useState('')

  // Adopt the server's copy after a save lands.
  useEffect(() => {
    setName(group.name)
    setDescription(group.description ?? '')
    setMemberIds(new Set(group.members.map(m => m.id)))
  }, [group])

  const sorted = useMemo(
    () =>
      [...candidates]
        .filter(c => `${c.name} ${c.email}`.toLowerCase().includes(filter.trim().toLowerCase()))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [candidates, filter],
  )
  const membersChanged =
    memberIds.size !== group.members.length || group.members.some(m => !memberIds.has(m.id))
  const detailsChanged = name.trim() !== group.name || (description.trim() || null) !== group.description

  const save = useMutation({
    mutationFn: async () => {
      if (detailsChanged) {
        await userGroupsApi.update(group.id, { name: name.trim(), description: description.trim() || null })
      }
      if (membersChanged) await userGroupsApi.setMembers(group.id, [...memberIds])
    },
    onSuccess: onSaved,
    onError,
  })
  const remove = useMutation({
    mutationFn: () => userGroupsApi.remove(group.id),
    onSuccess: onDeleted,
    onError,
  })

  return (
    <Card>
      <CardContent className='space-y-4 p-4'>
        <div className='grid gap-3 sm:grid-cols-2'>
          <div className='space-y-1.5'>
            <Label htmlFor='group-name' className='text-xs'>Name</Label>
            <Input id='group-name' value={name} onChange={e => setName(e.target.value)} className='h-8 text-sm' />
          </div>
          <div className='space-y-1.5'>
            <Label htmlFor='group-description' className='text-xs'>Description</Label>
            <Textarea
              id='group-description'
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={1}
              className='min-h-8 text-sm'
            />
          </div>
        </div>

        <div className='space-y-2'>
          <div className='flex items-center justify-between gap-2'>
            <p className='text-xs font-medium'>
              Members ({memberIds.size})
            </p>
            <Input
              value={filter}
              onChange={e => setFilter(e.target.value)}
              placeholder='Filter people…'
              aria-label='Filter people'
              className='h-7 w-48 text-xs'
            />
          </div>
          {candidatesLoading && <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />}
          <div className='max-h-80 space-y-0.5 overflow-y-auto rounded-md border p-2'>
            {sorted.map(c => (
              <label
                key={c.id}
                className='flex cursor-pointer items-center gap-2 rounded-sm px-1 py-1 text-sm hover:bg-accent'
              >
                <Checkbox
                  checked={memberIds.has(c.id)}
                  onCheckedChange={on =>
                    setMemberIds(prev => {
                      const next = new Set(prev)
                      if (on === true) next.add(c.id)
                      else next.delete(c.id)
                      return next
                    })
                  }
                />
                <span className='flex-1'>
                  {c.name}
                  {!c.enabled && <span className='ml-1 text-xs text-muted-foreground'>(deactivated)</span>}
                </span>
                <span className='text-xs text-muted-foreground'>{c.email}</span>
              </label>
            ))}
            {!candidatesLoading && sorted.length === 0 && (
              <p className='p-1 text-xs text-muted-foreground'>No tenant members match.</p>
            )}
          </div>
          <p className='text-[11px] text-muted-foreground'>
            Only members of this tenant can be added. Deactivated people stay listed but can never sign.
          </p>
        </div>

        <div className='flex items-center justify-between'>
          <Button
            type='button'
            variant='outline'
            size='sm'
            className='text-destructive'
            disabled={remove.isPending}
            onClick={() => {
              if (window.confirm(`Delete the group "${group.name}"? This is refused while any form uses it.`)) {
                remove.mutate()
              }
            }}
          >
            <Trash2 className='mr-1.5 h-3.5 w-3.5' /> Delete group
          </Button>
          <Button
            type='button'
            size='sm'
            disabled={(!membersChanged && !detailsChanged) || !name.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending && <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />}
            Save changes
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
